import { CSSProperties, ReactNode } from "react";
import classNames from "classnames";
import { flexRender, Subscribe, type RowData } from "@tanstack/react-table";
import type { ViewTableHeader, ViewTableLeafHeaderContext } from "./core/table";
import { isFillerColumn } from "./core/fillerColumns";
import { getPinClasses } from "./getPinClasses";
import styles from "./ViewTable.module.scss";
import {
    ColumnPinData,
    getLiftedHeaderTier,
    hasGroupBoundary,
} from "./ViewTable.lib";

interface HeaderGridStyleOptions {
    depth: number;
    colSpan: number;
    pinStyle: CSSProperties | undefined;
}

function getHeaderGridStyle({
    depth,
    colSpan,
    pinStyle,
}: HeaderGridStyleOptions): CSSProperties {
    return {
        ...pinStyle,
        gridRow: depth + 1,
        gridColumn: `span ${colSpan}`,
    };
}

function isOnGroupBoundary<TData extends RowData>(
    header: ViewTableHeader<TData, unknown>,
): boolean {
    return hasGroupBoundary(header.column.columnDef.meta);
}

interface CanResizeLeafHeaderOptions<TData extends RowData> {
    header: ViewTableHeader<TData, unknown>;
    enableColumnResizing: boolean;
}

function canResizeLeafHeader<TData extends RowData>({
    header,
    enableColumnResizing,
}: CanResizeLeafHeaderOptions<TData>): boolean {
    if (!enableColumnResizing) {
        return false;
    }
    const def = header.column.columnDef;
    const isFixedWidth =
        def.minSize !== undefined &&
        def.maxSize !== undefined &&
        def.minSize === def.maxSize;
    return (
        !isFillerColumn(def.id) && !isFixedWidth && header.column.getCanResize()
    );
}

interface HeaderCellCommon<TData extends RowData> {
    header: ViewTableHeader<TData, unknown>;
    depth: number;
}

interface PlaceholderHeaderCellProps<
    TData extends RowData,
> extends HeaderCellCommon<TData> {
    pinData: ColumnPinData | undefined;
    // Renders the column's label at this placeholder (a lifted leaf's true
    // tier); the cells below it stay blank.
    shouldShowTierLabel?: boolean;
}

export const PlaceholderHeaderCell = <TData extends RowData>({
    header,
    depth,
    pinData,
    shouldShowTierLabel = false,
}: PlaceholderHeaderCellProps<TData>) => (
    <div
        role={shouldShowTierLabel ? "columnheader" : undefined}
        className={classNames(header.column.id, styles.header, {
            // Skip groupPlaceholder (pointer-events: none) on label cells so
            // their truncation/Direct-Total tooltips stay interactive.
            [styles.groupPlaceholder]: !shouldShowTierLabel,
            ...getPinClasses(pinData),
            [styles.groupBoundary]: isOnGroupBoundary(header),
        })}
        style={getHeaderGridStyle({
            depth,
            colSpan: header.colSpan,
            pinStyle: pinData?.style,
        })}
        data-placeholder-header-cell=""
        data-header-tier-label={shouldShowTierLabel ? "" : undefined}
        aria-hidden={shouldShowTierLabel ? undefined : "true"}
    >
        {shouldShowTierLabel
            ? flexRender(header.column.columnDef.header, header.getContext())
            : null}
    </div>
);

interface GroupHeaderCellProps<
    TData extends RowData,
> extends HeaderCellCommon<TData> {
    pinData?: ColumnPinData;
}

export const GroupHeaderCell = <TData extends RowData>({
    header,
    depth,
    pinData,
}: GroupHeaderCellProps<TData>) => (
    <div
        role="columnheader"
        aria-colspan={header.colSpan > 1 ? header.colSpan : undefined}
        className={classNames(
            header.column.id,
            styles.header,
            styles.groupHeader,
            {
                ...getPinClasses(pinData),
                [styles.groupBoundary]: isOnGroupBoundary(header),
            },
        )}
        style={getHeaderGridStyle({
            depth,
            colSpan: header.colSpan,
            pinStyle: pinData?.style,
        })}
        data-group-header-cell=""
    >
        <span
            className={classNames(styles.groupHeaderLabel, {
                [styles.pinnedGroupHeaderLabel]: Boolean(pinData?.isPinned),
            })}
            data-group-header-label=""
        >
            {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
    </div>
);

interface LeafHeaderCellProps<
    TData extends RowData,
> extends HeaderCellCommon<TData> {
    pinData: ColumnPinData | undefined;
    enableColumnResizing: boolean;
    headerVariantMenu: ReactNode;
    headerRowCount: number;
    isAllPageRowsSelected: boolean;
    isSomePageRowsSelected: boolean;
}

export const LeafHeaderCell = <TData extends RowData>({
    header,
    depth,
    pinData,
    enableColumnResizing,
    headerVariantMenu,
    headerRowCount,
    isAllPageRowsSelected,
    isSomePageRowsSelected,
}: LeafHeaderCellProps<TData>) => {
    const canResize = canResizeLeafHeader({ header, enableColumnResizing });

    const isAlignRight = header.column.columnDef.meta?.align === "right";

    // A lifted leaf's label is rendered by its tier placeholder above; this
    // bottom cell stays in place (to hold its grid column slot) but blank, and
    // drops out of the a11y tree so it is not an empty duplicate columnheader.
    const isLifted =
        getLiftedHeaderTier(header.column.columnDef.meta, headerRowCount) !==
        undefined;

    return (
        <div
            role={isLifted ? undefined : "columnheader"}
            aria-hidden={isLifted ? "true" : undefined}
            className={classNames(header.column.id, styles.header, {
                ...getPinClasses(pinData),
                [styles.selectColumn]: header.column.id === "select",
                [styles.alignRight]: isAlignRight,
                [styles.groupBoundary]: isOnGroupBoundary(header),
            })}
            data-header-cell=""
            style={getHeaderGridStyle({
                depth,
                colSpan: header.colSpan,
                pinStyle: pinData?.style,
            })}
        >
            {!isLifted &&
                flexRender(header.column.columnDef.header, {
                    ...header.getContext(),
                    isAllPageRowsSelected,
                    isSomePageRowsSelected,
                } satisfies ViewTableLeafHeaderContext<TData>)}
            {!isLifted && headerVariantMenu}
            {canResize && (
                // `header` stays stable while a drag starts and ends, so the
                // resizing state is read inside `Subscribe`.
                <Subscribe
                    source={header.column.table.atoms.columnResizing}
                    selector={(columnResizing) =>
                        columnResizing.isResizingColumn
                    }
                >
                    {() => (
                        <div
                            onDoubleClick={header.column.resetSize}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={classNames(styles.resizer, {
                                [styles.isResizing]:
                                    header.column.getIsResizing(),
                            })}
                        />
                    )}
                </Subscribe>
            )}
        </div>
    );
};
